import { parseOptionalDurationMinutes } from "../../../../../../../../../../../_shared/_logic/work-order-form-utils"
import type { WorkOrderFormValue } from "../../../../../../../../../../../_shared/_logic/work-order-form-types"
import {
  hasCompleteOrderTypeSelection,
  orderTypeSelectionPayload,
} from "../../../../../../../../../../../_shared/_logic/order-type-selection"
import { defaultPlanningDueDate } from "../planning-due-date-preset"
export const MAX_PREPARATION_DURATION_MINUTES = 480
export type WorkOrderCreationFields = Pick<
  WorkOrderFormValue,
  | "order_type_ids"
  | "custom_order_type"
  | "estimated_duration_min"
  | "allowed_employee_ids"
  | "due_date"
>
export const copyCreationFields = (value: WorkOrderCreationFields): WorkOrderCreationFields => ({
  ...value,
  allowed_employee_ids: [...value.allowed_employee_ids],
  order_type_ids: [...value.order_type_ids],
  custom_order_type: value.custom_order_type ? { ...value.custom_order_type } : null,
})
export const defaultCreationFields = (
  orderTypes: OrderType[],
  employeeIds: string[],
  timezone?: string,
): WorkOrderCreationFields => ({
  order_type_ids: orderTypes[0]?.id ? [orderTypes[0].id] : [],
  custom_order_type: null,
  estimated_duration_min: orderTypes[0]?.typical_duration_min
    ? String(orderTypes[0].typical_duration_min)
    : "",
  due_date: defaultPlanningDueDate(new Date(), timezone),
  allowed_employee_ids: [...employeeIds],
})
/**
 * Order fields a preconfiguration sets on its target rows. An omitted field keeps the row value;
 * order type ids and free-text type always change together.
 */
export type WorkOrderCreationFieldsPatch = {
  orderTypeSelection?: Pick<WorkOrderCreationFields, "order_type_ids" | "custom_order_type">
  estimated_duration_min?: string
  allowed_employee_ids?: string[]
  due_date?: string
}
export const applyCreationFieldsPatch = (
  value: WorkOrderCreationFields,
  patch: WorkOrderCreationFieldsPatch,
): WorkOrderCreationFields =>
  copyCreationFields({
    ...value,
    ...patch.orderTypeSelection,
    estimated_duration_min: patch.estimated_duration_min ?? value.estimated_duration_min,
    allowed_employee_ids: patch.allowed_employee_ids ?? value.allowed_employee_ids,
    due_date: patch.due_date ?? value.due_date,
  })
const orderTypeSelectionError = (
  selection: Pick<WorkOrderCreationFields, "order_type_ids" | "custom_order_type">,
): string | null =>
  hasCompleteOrderTypeSelection(selection) ? null : "Bitte einen Auftragstyp wählen."
const allowedEmployeesError = (employeeIds: string[]): string | null =>
  employeeIds.length ? null : "Bitte mindestens einen zugelassenen Mitarbeiter wählen."
const durationError = (duration: string): string | null => {
  if (!duration) return null

  const minutes = Number(duration)

  return Number.isInteger(minutes) && minutes >= 1 && minutes <= MAX_PREPARATION_DURATION_MINUTES
    ? null
    : `Die Dauer muss zwischen 1 und ${MAX_PREPARATION_DURATION_MINUTES} Minuten liegen.`
}
const dueDateError = (dueDate: string): string | null => {
  if (!dueDate) return null

  return /^\d{4}-\d{2}-\d{2}$/.test(dueDate) &&
    Number.isFinite(Date.parse(dueDate)) &&
    new Date(dueDate).toISOString().slice(0, 10) === dueDate
    ? null
    : "Bitte eine gültige Auftragsfrist wählen."
}
export const validateCreationFields = (value: WorkOrderCreationFields): string | null =>
  orderTypeSelectionError(value) ??
  allowedEmployeesError(value.allowed_employee_ids) ??
  (!value.estimated_duration_min ? "Bitte eine Dauer eintragen." : durationError(value.estimated_duration_min)) ??
  dueDateError(value.due_date)
/** Validates only the fields a preconfiguration actually sets. */
export const validateCreationFieldsPatch = (patch: WorkOrderCreationFieldsPatch): string | null =>
  (patch.orderTypeSelection ? orderTypeSelectionError(patch.orderTypeSelection) : null) ??
  (patch.allowed_employee_ids ? allowedEmployeesError(patch.allowed_employee_ids) : null) ??
  (patch.estimated_duration_min !== undefined
    ? durationError(patch.estimated_duration_min)
    : null) ??
  (patch.due_date !== undefined ? dueDateError(patch.due_date) : null)
export const creationPayload = (
  propertyId: string,
  propertyUnitId: string,
  value: WorkOrderCreationFields,
): WorkOrderCreatePayload => ({
  property_id: propertyId,
  property_unit_id: propertyUnitId,
  ...orderTypeSelectionPayload(value),
  estimated_duration_min: parseOptionalDurationMinutes(value.estimated_duration_min),
  allowed_employee_ids: [...value.allowed_employee_ids],
  due_date: value.due_date || null,
})
